import React, {useEffect, useMemo, useRef, useState} from "react";
import {GoogleMap, InfoWindow, LoadScript, Marker} from "@react-google-maps/api";
import {Link, useHistory} from "react-router-dom";
import Colors from "../../Constants/Colors";
import {postData} from "../../Utils/Server";
import Endpoints from "../../Constants/Endpoints";
import Strings from "../../Constants/Strings";
import {updateDocumentTitle} from "../../Utils/Common";
import PageLayout from "../Layout/PageLayout";
import {Button, Spinner, TextInput} from "../UI";
import {tokens} from "../../theme/tokens";

const POLL_MS = 15000;
const DEFAULT_CENTER = {lat: -1.286389, lng: 36.817223}; // Nairobi as a sane fallback

const STATUS = {
    ON_JOB: {key: "ON_JOB", label: "On a job", color: Colors.deep_orange_600},
    ASSIGNED: {key: "ASSIGNED", label: "Assigned", color: Colors.blue_600},
    AVAILABLE: {key: "AVAILABLE", label: "Available", color: Colors.green_600},
    ONLINE: {key: "ONLINE", label: "Online", color: Colors.grey_600},
    NO_GPS: {key: "NO_GPS", label: "No GPS", color: Colors.grey_500},
};

const hasValidCoords = r =>
    typeof r.current_lat === "number" &&
    typeof r.current_lng === "number" &&
    !(r.current_lat === 0 && r.current_lng === 0);

const classifyRider = r => {
    if (!hasValidCoords(r)) return STATUS.NO_GPS;
    if ((r.ongoing_jobs || 0) > 0 || (r.current_jobs || 0) > 0) return STATUS.ON_JOB;
    if ((r.assigned_jobs || 0) > 0) return STATUS.ASSIGNED;
    if (r.available === 1) return STATUS.AVAILABLE;
    return STATUS.ONLINE;
};

const buildMarkerIcon = (status, bearing) => {
    if (typeof window === "undefined" || !window.google || !window.google.maps) return undefined;
    const moving = status.key === "ON_JOB" && typeof bearing === "number" && bearing !== 0;
    if (moving) {
        return {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 5,
            fillColor: status.color,
            fillOpacity: 1,
            strokeColor: Colors.white,
            strokeWeight: 2,
            rotation: bearing,
        };
    }
    return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: status.color,
        fillOpacity: 1,
        strokeColor: Colors.white,
        strokeWeight: 2,
    };
};

const LiveMap = props => {
    const history = useHistory();

    const [riders, setRiders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("ALL");
    const [selectedId, setSelectedId] = useState(null);
    const [isPaused, setIsPaused] = useState(false);
    const [now, setNow] = useState(Date.now());

    const mapRef = useRef(null);
    const lastLoadedRef = useRef(Date.now());
    const hasFitRef = useRef(false);
    const isPausedRef = useRef(false);

    useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

    const load = () => {
        const data = new FormData();
        data.append("status", 1);
        setIsLoading(true);
        postData(Endpoints.RIDER_LIST, data)
            .then(res => res.json())
            .then(res => {
                setIsLoading(false);
                const list = Array.isArray(res.data) ? res.data : [];
                setRiders(list);
                lastLoadedRef.current = Date.now();
            })
            .catch(error => {
                setIsLoading(false);
                console.log(error);
            });
    };

    useEffect(() => {
        updateDocumentTitle("Live map");
        load();

        const reloadId = setInterval(() => {
            if (!isPausedRef.current) load();
        }, POLL_MS);
        const tickId = setInterval(() => setNow(Date.now()), 1000);

        return () => {
            clearInterval(reloadId);
            clearInterval(tickId);
        };
    }, []);

    const counts = useMemo(() => {
        const acc = {ALL: riders.length, ON_JOB: 0, ASSIGNED: 0, AVAILABLE: 0, ONLINE: 0, NO_GPS: 0};
        riders.forEach(r => { acc[classifyRider(r).key] += 1; });
        return acc;
    }, [riders]);

    const matchedRiders = useMemo(() => {
        const q = search.trim().toLowerCase();
        return riders.filter(r => {
            if (filter !== "ALL" && classifyRider(r).key !== filter) return false;
            if (!q) return true;
            return [r.name, r.rider_code, r.phone, r.current_location]
                .filter(Boolean)
                .some(v => String(v).toLowerCase().includes(q));
        });
    }, [riders, filter, search]);

    const mappableMatched = useMemo(() => matchedRiders.filter(hasValidCoords), [matchedRiders]);

    useEffect(() => {
        if (hasFitRef.current) return;
        if (!mapRef.current || !window.google || mappableMatched.length === 0) return;
        const bounds = new window.google.maps.LatLngBounds();
        mappableMatched.forEach(r => bounds.extend({lat: r.current_lat, lng: r.current_lng}));
        if (mappableMatched.length === 1) {
            mapRef.current.setCenter(bounds.getCenter());
            mapRef.current.setZoom(14);
        } else {
            mapRef.current.fitBounds(bounds, 60);
        }
        hasFitRef.current = true;
    }, [mappableMatched]);

    const selectedRider = useMemo(
        () => riders.find(r => r.id === selectedId) || null,
        [riders, selectedId]
    );

    const focusRider = rider => {
        setSelectedId(rider.id);
        if (hasValidCoords(rider) && mapRef.current) {
            mapRef.current.panTo({lat: rider.current_lat, lng: rider.current_lng});
            const z = mapRef.current.getZoom();
            if (!z || z < 13) mapRef.current.setZoom(14);
        }
    };

    const lastUpdatedLabel = (() => {
        const diff = Math.floor((now - lastLoadedRef.current) / 1000);
        if (diff < 5) return "just now";
        if (diff < 60) return `${diff}s ago`;
        const m = Math.floor(diff / 60);
        return `${m} min${m === 1 ? "" : "s"} ago`;
    })();

    const noGpsCount = counts.NO_GPS;

    const containerStyle = {width: "100%", height: "100%"};
    const mapHeight = "calc(100vh - 280px)";

    return (
        <PageLayout
            title="Live Map"
            actions={
                <>
                    {isLoading && <Spinner size={20}/>}
                    <Button
                        size="sm"
                        variant={isPaused ? "primary" : "outline"}
                        onClick={() => setIsPaused(p => !p)}
                    >
                        {isPaused ? "Resume" : "Pause"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => load()}>Reload</Button>
                </>
            }
        >
            <div style={{
                fontSize: tokens.typography.size.sm,
                color: tokens.colors.muted,
                marginBottom: 12,
            }}>
                {counts.ALL} rider{counts.ALL === 1 ? "" : "s"} online · last updated {lastUpdatedLabel}
                {noGpsCount > 0 && <span> · {noGpsCount} without GPS</span>}
            </div>

            <div style={{display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12}}>
                {[
                    {key: "ALL", label: "All", color: tokens.colors.heading, count: counts.ALL},
                    {key: "AVAILABLE", label: "Available", color: STATUS.AVAILABLE.color, count: counts.AVAILABLE},
                    {key: "ON_JOB", label: "On a job", color: STATUS.ON_JOB.color, count: counts.ON_JOB},
                    {key: "ASSIGNED", label: "Assigned", color: STATUS.ASSIGNED.color, count: counts.ASSIGNED},
                    {key: "ONLINE", label: "Idle", color: STATUS.ONLINE.color, count: counts.ONLINE},
                    {key: "NO_GPS", label: "No GPS", color: STATUS.NO_GPS.color, count: counts.NO_GPS},
                ].map(chip => {
                    const active = filter === chip.key;
                    return (
                        <button
                            key={chip.key}
                            onClick={() => setFilter(chip.key)}
                            style={{
                                border: `1px solid ${chip.color}`,
                                backgroundColor: active ? chip.color : tokens.colors.surface,
                                color: active ? tokens.colors.onDark : chip.color,
                                padding: "6px 14px",
                                borderRadius: tokens.radii.pill,
                                fontSize: tokens.typography.size.sm,
                                fontWeight: tokens.typography.weight.semibold,
                                cursor: "pointer",
                            }}
                        >
                            {chip.label} · {chip.count}
                        </button>
                    );
                })}
            </div>

            <div style={{display: "flex", gap: 12, alignItems: "stretch", height: mapHeight, minHeight: 500}}>

                <aside style={{
                    flex: "0 0 320px",
                    backgroundColor: tokens.colors.surface,
                    border: `1px solid ${tokens.colors.border}`,
                    borderRadius: tokens.radii.lg,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}>
                    <div style={{padding: 10, borderBottom: `1px solid ${tokens.colors.border}`}}>
                        <TextInput
                            type="text"
                            $size="sm"
                            placeholder="Search riders by name, code, phone…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div style={{flex: 1, overflowY: "auto"}}>
                        {matchedRiders.length === 0 &&
                        <div style={{padding: 20, textAlign: "center", color: tokens.colors.muted, fontSize: tokens.typography.size.sm}}>
                            No riders match.
                        </div>
                        }
                        {matchedRiders.map(r => {
                            const status = classifyRider(r);
                            const noGps = status.key === "NO_GPS";
                            const active = r.id === selectedId;
                            return (
                                <div key={r.id}>
                                    <div
                                        onClick={() => focusRider(r)}
                                        style={{
                                            display: "flex",
                                            gap: 10,
                                            padding: "10px 12px",
                                            borderBottom: `1px solid ${tokens.colors.divider}`,
                                            cursor: "pointer",
                                            backgroundColor: active ? tokens.colors.rose : tokens.colors.surface,
                                            borderLeft: noGps
                                                ? `3px dashed ${status.color}`
                                                : `3px solid ${status.color}`,
                                            opacity: noGps ? 0.85 : 1,
                                        }}
                                    >
                                        <div style={{
                                            width: 36, height: 36, borderRadius: "50%",
                                            backgroundColor: tokens.colors.surfaceSunken,
                                            backgroundImage: r.photo ? `url(${Endpoints.SERVER_HOST}${Endpoints.FILE_URL}${r.photo})` : "none",
                                            backgroundSize: "cover", backgroundPosition: "center",
                                            flexShrink: 0,
                                            filter: noGps ? "grayscale(0.5)" : "none",
                                        }}/>
                                        <div style={{flex: 1, minWidth: 0}}>
                                            <div style={{fontWeight: 600, fontSize: 13, color: tokens.colors.heading, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                                {r.name || "Unnamed"}
                                            </div>
                                            <div style={{fontSize: 11, color: tokens.colors.muted, marginTop: 2, display: "flex", alignItems: "center", gap: 6}}>
                                                <span>#{r.rider_code}</span>
                                                <span>·</span>
                                                {noGps
                                                    ? <span style={{
                                                        fontWeight: 700, color: tokens.colors.onDark,
                                                        backgroundColor: status.color,
                                                        padding: "1px 6px", borderRadius: 8, fontSize: 10,
                                                    }}>NO GPS</span>
                                                    : <span>{status.label}</span>
                                                }
                                            </div>
                                            {(r.ongoing_jobs > 0 || r.assigned_jobs > 0) &&
                                            <div style={{fontSize: 11, color: tokens.colors.muted, marginTop: 2}}>
                                                {r.ongoing_jobs > 0 && <span>Ongoing: {r.ongoing_jobs}</span>}
                                                {r.ongoing_jobs > 0 && r.assigned_jobs > 0 && <span> · </span>}
                                                {r.assigned_jobs > 0 && <span>Assigned: {r.assigned_jobs}</span>}
                                            </div>
                                            }
                                        </div>
                                    </div>
                                    {active && noGps &&
                                    <div style={{
                                        padding: 12,
                                        backgroundColor: tokens.colors.surfaceMuted,
                                        borderBottom: `1px solid ${tokens.colors.border}`,
                                    }}>
                                        <div style={{
                                            fontSize: 11, color: tokens.colors.muted,
                                            marginBottom: 8, fontStyle: "italic",
                                        }}>
                                            This rider is online but has not reported a GPS position.
                                        </div>
                                        <RiderInfoCard rider={r} onView={() => history.push("/riders/" + r.id)}/>
                                    </div>
                                    }
                                </div>
                            );
                        })}
                    </div>
                </aside>

                <div style={{flex: 1, border: `1px solid ${tokens.colors.border}`, borderRadius: tokens.radii.lg, overflow: "hidden"}}>
                    <LoadScriptOnlyIfNeeded googleMapsApiKey={Strings.GOOGLE_API_KEY} id={1}>
                        <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={DEFAULT_CENTER}
                            zoom={11}
                            onLoad={map => { mapRef.current = map; }}
                            onClick={() => setSelectedId(null)}
                            options={{
                                streetViewControl: false,
                                mapTypeControl: false,
                                fullscreenControl: true,
                            }}
                        >
                            {mappableMatched.map(r => {
                                const status = classifyRider(r);
                                return (
                                    <Marker
                                        key={r.id}
                                        position={{lat: r.current_lat, lng: r.current_lng}}
                                        icon={buildMarkerIcon(status, r.current_bearing)}
                                        title={`${r.name} · ${status.label}`}
                                        onClick={() => setSelectedId(r.id)}
                                    />
                                );
                            })}

                            {selectedRider && hasValidCoords(selectedRider) &&
                            <InfoWindow
                                position={{lat: selectedRider.current_lat, lng: selectedRider.current_lng}}
                                onCloseClick={() => setSelectedId(null)}
                                options={{pixelOffset: window.google && new window.google.maps.Size(0, -10)}}
                            >
                                <RiderInfoCard rider={selectedRider} onView={() => history.push("/riders/" + selectedRider.id)}/>
                            </InfoWindow>
                            }
                        </GoogleMap>
                    </LoadScriptOnlyIfNeeded>
                </div>
            </div>

            <div style={{marginTop: 10, display: "flex", flexWrap: "wrap", gap: 14, fontSize: tokens.typography.size.sm, color: tokens.colors.muted}}>
                <LegendDot color={STATUS.AVAILABLE.color} label="Available"/>
                <LegendDot color={STATUS.ON_JOB.color} label="On a job (arrow shows bearing)"/>
                <LegendDot color={STATUS.ASSIGNED.color} label="Assigned"/>
                <LegendDot color={STATUS.ONLINE.color} label="Idle / online"/>
                <LegendDot color={STATUS.NO_GPS.color} label="No GPS (sidebar only)" dashed/>
            </div>
        </PageLayout>
    );
};

const LegendDot = ({color, label, dashed}) => (
    <span style={{display: "inline-flex", alignItems: "center", gap: 6}}>
        <span style={{
            width: 10, height: 10, borderRadius: "50%",
            backgroundColor: dashed ? "transparent" : color,
            border: dashed ? `1.5px dashed ${color}` : "none",
            display: "inline-block",
        }}/>
        {label}
    </span>
);

const RiderInfoCard = ({rider, onView}) => {
    const status = classifyRider(rider);
    const photoUrl = rider.photo ? `${Endpoints.SERVER_HOST}${Endpoints.FILE_URL}${rider.photo}` : null;
    return (
        <div style={{minWidth: 240, padding: "4px 4px 8px", color: tokens.colors.heading}}>
            <div style={{display: "flex", gap: 10, alignItems: "center", marginBottom: 8}}>
                <div style={{
                    width: 44, height: 44, borderRadius: "50%",
                    backgroundColor: tokens.colors.surfaceSunken,
                    backgroundImage: photoUrl ? `url(${photoUrl})` : "none",
                    backgroundSize: "cover", backgroundPosition: "center",
                    flexShrink: 0,
                }}/>
                <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontWeight: 700, fontSize: 14}}>{rider.name || "Unnamed"}</div>
                    <div style={{fontSize: 12, color: tokens.colors.muted}}>#{rider.rider_code}</div>
                </div>
                <span style={{
                    fontSize: 11, fontWeight: 700, color: tokens.colors.onDark,
                    backgroundColor: status.color, padding: "3px 8px", borderRadius: 10,
                }}>
                    {status.label}
                </span>
            </div>

            {rider.current_location &&
            <div style={{fontSize: 12, color: tokens.colors.muted, marginBottom: 6}}>{rider.current_location}</div>
            }

            <div style={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, fontSize: 12, marginBottom: 8}}>
                <Stat label="Ongoing" value={rider.ongoing_jobs || 0}/>
                <Stat label="Assigned" value={rider.assigned_jobs || 0}/>
                <Stat label="Done" value={rider.completed_jobs || 0}/>
            </div>

            {typeof rider.ratings === "number" && rider.ratings > 0 &&
            <div style={{fontSize: 12, color: tokens.colors.muted, marginBottom: 8}}>
                Rating: <b>{rider.ratings.toFixed(1)}</b>
            </div>
            }

            <Link to={"/riders/" + rider.id} onClick={onView} style={{textDecoration: "none"}}>
                <Button size="sm">View profile</Button>
            </Link>
        </div>
    );
};

const Stat = ({label, value}) => (
    <div style={{textAlign: "center", padding: "4px 0", backgroundColor: tokens.colors.surfaceMuted, borderRadius: 3}}>
        <div style={{fontWeight: 700, fontSize: 14, color: tokens.colors.heading}}>{value}</div>
        <div style={{fontSize: 10, color: tokens.colors.muted, textTransform: "uppercase", letterSpacing: 0.4}}>{label}</div>
    </div>
);

class LoadScriptOnlyIfNeeded extends LoadScript {
    componentDidMount() {
        const cleaningUp = true;
        const isBrowser = typeof document !== "undefined";
        const isAlreadyLoaded =
            window.google &&
            window.google.maps &&
            document.querySelector("body.first-hit-completed");
        if (!isAlreadyLoaded && isBrowser) {
            if (window.google && !cleaningUp) {
                console.error("google api is already presented");
                return;
            }
            this.isCleaningUp().then(this.injectScript);
        }
        if (isAlreadyLoaded) {
            this.setState({loaded: true});
        }
    }
}

export default LiveMap;
